import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppRoot } from '@telegram-apps/telegram-ui';
import { SwipeableTabs } from './features/tabs/SwipeableTabs';
import { CatalogIcon, CartIcon, ChatIcon, ProfileIcon, PurchasesIcon } from './features/tabs/icons';
import type { TabDefinition } from './features/tabs/BottomNavigation';
import { MenuButton, SideDrawer } from './features/menu/SideDrawer';
import { BrandMark } from './brand/BrandMark';
import { CartProvider, useCart } from './features/cart/CartContext';
import { CatalogPage } from './pages/CatalogPage';
import { CartSheet } from './pages/CartSheet';
import { PurchasesPage } from './pages/PurchasesPage';
import { ProfilePage } from './pages/ProfilePage';
import { ChatDesk } from './features/chat/ChatDesk';
import { AdminPage } from './pages/AdminPage';
import { ErrorState, Splash } from './components/States';
import { marketplaceApi, type CatalogKind, type PaymentOptions } from './api/marketplace';
import { chatApi } from './api/chat';
import { ensureSession, getSessionUser } from './lib/api';
import { getWebApp, isInsideTelegram } from './lib/telegram';

const CART_TAB = 1;
const CHAT_TAB = 4;

function readChatDeepLink(): string | null {
  const query = new URLSearchParams(window.location.search).get('chat');
  if (query && /^[0-9a-f-]{36}$/i.test(query)) return query;

  const start = getWebApp()?.initDataUnsafe.start_param ?? '';
  const bare = start.replace(/-/g, '');
  if (/^[0-9a-f]{32}$/i.test(bare)) {
    return `${bare.slice(0, 8)}-${bare.slice(8, 12)}-${bare.slice(12, 16)}-${bare.slice(16, 20)}-${bare.slice(20)}`;
  }

  return null;
}

export function App() {
  return (
    <CartProvider>
      <AppShell />
    </CartProvider>
  );
}

function AppShell() {
  const cart = useCart();
  const [authState, setAuthState] = useState<'pending' | 'ready' | 'failed'>('pending');
  const [authError, setAuthError] = useState<string>('');
  const [purchaseVersion, setPurchaseVersion] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [catalogKind, setCatalogKind] = useState<CatalogKind | undefined>();
  const [tabIndex, setTabIndex] = useState(0);
  const [tabToken, setTabToken] = useState(0);
  const [cartToken, setCartToken] = useState(0);
  const [payments, setPayments] = useState<PaymentOptions>({ tribute: false, stars: true, minorPerStar: 200 });
  const [unreadChats, setUnreadChats] = useState(0);
  const [focusOrderId, setFocusOrderId] = useState<string | null>(null);
  const [focusChatId, setFocusChatId] = useState<string | null>(null);
  const deepLinkDone = useRef(false);

  const tabs = useMemo<TabDefinition[]>(
    () => [
      { id: 'catalog', label: 'Каталог', icon: <CatalogIcon /> },
      { id: 'cart', label: 'Корзина', icon: <CartIcon />, badge: cart.count || undefined },
      { id: 'purchases', label: 'Инвентарь', icon: <PurchasesIcon /> },
      { id: 'profile', label: 'Профиль', icon: <ProfileIcon /> },
      { id: 'chats', label: 'Чат', icon: <ChatIcon />, badge: unreadChats || undefined },
    ],
    [cart.count, unreadChats],
  );

  const goTab = (index: number): void => {
    setAdminOpen(false);
    setMenuOpen(false);
    setTabIndex(index);
    setTabToken((value) => value + 1);
  };

  const authenticate = useCallback(() => {
    setAuthState('pending');

    ensureSession()
      .then(() => setAuthState('ready'))
      .catch((cause: unknown) => {
        setAuthError(cause instanceof Error ? cause.message : 'Не удалось авторизоваться');
        setAuthState('failed');
      });
  }, []);

  useEffect(authenticate, [authenticate]);

  useEffect(() => {
    marketplaceApi.payments().then(setPayments).catch(() => undefined);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('paid') === '1') {
      setPurchaseVersion((value) => value + 1);
    }
  }, []);

  useEffect(() => {
    if (authState !== 'ready' || deepLinkDone.current) return;

    const chatId = readChatDeepLink();
    if (!chatId) return;

    deepLinkDone.current = true;
    setFocusChatId(chatId);
    setAdminOpen(false);
    setTabIndex(CHAT_TAB);
    setTabToken((value) => value + 1);
  }, [authState]);

  useEffect(() => {
    if (authState !== 'ready') return undefined;

    let stop = false;

    const tick = (): void => {
      if (document.visibilityState === 'hidden') return;
      chatApi
        .unread()
        .then((response) => {
          if (!stop) setUnreadChats(response.count);
        })
        .catch(() => undefined);
    };

    tick();
    const timer = window.setInterval(tick, 15_000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [authState]);

  const onPurchased = (orderId?: string): void => {
    setPurchaseVersion((version) => version + 1);
    if (!orderId) return;
    setFocusOrderId(orderId);
    goTab(CHAT_TAB);
  };

  const webApp = getWebApp();
  const isAdmin = getSessionUser()?.role === 'ADMIN';

  const openCatalog = (kind?: CatalogKind): void => {
    setCatalogKind(kind);
    goTab(0);
  };

  const openCart = (): void => {
    setCartToken((value) => value + 1);
    goTab(CART_TAB);
  };

  if (authState === 'pending') return <Splash />;

  if (authState === 'failed') {
    return (
      <ErrorState
        message={
          isInsideTelegram()
            ? authError
            : 'Открой приложение через бота в Telegram — вне него нет данных для входа.'
        }
        onRetry={authenticate}
      />
    );
  }

  return (
    <AppRoot appearance="dark" platform={webApp?.platform === 'ios' ? 'ios' : 'base'}>
      <header className="app-chrome">
        <MenuButton onClick={() => setMenuOpen(true)} />
        <BrandMark />
        <CartButton onClick={openCart} />
      </header>
      <SideDrawer
        open={menuOpen}
        isAdmin={isAdmin}
        onClose={() => setMenuOpen(false)}
        onOpenKind={(kind) => openCatalog(kind)}
        onOpenAdmin={() => {
          setMenuOpen(false);
          setAdminOpen(true);
        }}
      />

      {adminOpen ? (
        <div className="admin-overlay">
          <button type="button" className="admin-overlay__back" onClick={() => setAdminOpen(false)}>
            ← Назад
          </button>
          <AdminPage />
        </div>
      ) : (
        <SwipeableTabs tabs={tabs} requestedIndex={tabIndex} requestToken={tabToken}>
          {[
            <CatalogPage
              key={`catalog-${catalogKind ?? 'all'}`}
              kind={catalogKind}
              onKindChange={(kind) => {
                setCatalogKind(kind);
                goTab(0);
              }}
              payments={payments}
              cartCloseToken={cartToken}
              onOpenCart={openCart}
              onPurchased={onPurchased}
            />,
            <CartSheet key="cart" payments={payments} onPurchased={onPurchased} />,
            <PurchasesPage
              key="purchases"
              refreshKey={purchaseVersion}
              onOpenChat={(orderId) => {
                setFocusOrderId(orderId);
                goTab(CHAT_TAB);
              }}
            />,
            <ProfilePage key="profile" />,
            <ChatDesk
              key="chats"
              mode="user"
              focusOrderId={focusOrderId}
              focusChatId={focusChatId}
              onFocusConsumed={() => {
                setFocusOrderId(null);
                setFocusChatId(null);
              }}
            />,
          ]}
        </SwipeableTabs>
      )}
    </AppRoot>
  );
}

function CartButton({ onClick }: { onClick: () => void }) {
  const { count } = useCart();

  return (
    <button type="button" className="cart-button" onClick={onClick} aria-label="Корзина">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M4 7h16l-1.2 12.1a2 2 0 0 1-2 1.9H7.2a2 2 0 0 1-2-1.9L4 7Z" strokeLinejoin="round" />
        <path d="M8.5 7V5.5a3.5 3.5 0 0 1 7 0V7" strokeLinecap="round" />
      </svg>
      {count > 0 ? <span className="cart-button__badge">{count}</span> : null}
    </button>
  );
}
